import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import {
  legalService,
  PLATFORM_SERVICE_CONTRACT_KEY,
  triggerBlobDownload,
  type ContractCoverageRow,
  type ContractSignatureRequestRow,
  type LegalBlock,
  type LegalTemplate,
  type SignedDocumentSummary,
} from '../../../services/legalService';
import { LegalBlocks } from '../../../components/legal/LegalBlocks';

function formatDate(value?: string | Date | null) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
}

const AdminLegalContracts: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [listFilter, setListFilter] = useState<'needs' | 'signed' | 'all'>('needs');

  const [coverage, setCoverage] = useState<ContractCoverageRow[]>([]);
  const [summary, setSummary] = useState({ total: 0, current: 0, outdated: 0, pending: 0, currentVersion: '' });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [signedDocs, setSignedDocs] = useState<SignedDocumentSummary[]>([]);
  const [catalog, setCatalog] = useState<LegalTemplate[]>([]);
  const [requests, setRequests] = useState<ContractSignatureRequestRow[]>([]);

  const [confirm, setConfirm] = useState<{ open: boolean; ids: string[]; label: string }>({
    open: false,
    ids: [],
    label: '',
  });
  const [note, setNote] = useState('');

  const [viewOpen, setViewOpen] = useState(false);
  const [viewTitle, setViewTitle] = useState('');
  const [viewBlocks, setViewBlocks] = useState<LegalBlock[]>([]);
  const [viewMeta, setViewMeta] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cov, signed, cat, reqs] = await Promise.all([
        legalService.listContractCoverage(PLATFORM_SERVICE_CONTRACT_KEY),
        legalService.listSignedDocuments({ templateKey: PLATFORM_SERVICE_CONTRACT_KEY, kind: 'contract' }),
        legalService.listLegalCatalogAdmin(),
        legalService.listContractSignatureRequests(),
      ]);
      setCoverage(cov.data || []);
      setSummary({
        total: cov.summary?.total || 0,
        current: cov.summary?.current || 0,
        outdated: cov.summary?.outdated || 0,
        pending: cov.summary?.pending || 0,
        currentVersion: cov.summary?.currentVersion || '',
      });
      setSignedDocs(signed);
      setCatalog(cat);
      setRequests(reqs);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Não foi possível carregar os contratos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const visibleRows = useMemo(() => {
    if (listFilter === 'needs') return coverage.filter((row) => !row.isCurrent);
    if (listFilter === 'signed') return coverage.filter((row) => row.isCurrent);
    return coverage;
  }, [coverage, listFilter]);

  const visibleIds = visibleRows.map((row) => row.parishId);
  const selectedVisible = selectedIds.filter((id) => visibleIds.includes(id));
  const allVisibleSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const openViewSigned = async (id: string) => {
    try {
      const doc = await legalService.getSignedDocumentAdmin(id);
      setViewTitle(doc.title || 'Contrato');
      setViewBlocks(doc.bodySnapshot || []);
      setViewMeta(
        `${doc.organizationName || ''} · ${doc.signerName || ''} · v${doc.templateVersion || ''} · ${formatDate(doc.signedAt)}`
      );
      setViewOpen(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Não foi possível abrir o contrato.');
    }
  };

  const downloadSigned = async (id: string, name: string) => {
    try {
      const blob = await legalService.downloadSignedDocumentAdmin(id);
      triggerBlobDownload(blob, `${name || 'contrato'}.pdf`);
    } catch {
      setError('Não foi possível baixar o PDF.');
    }
  };

  const openConfirm = (ids: string[], label: string) => {
    if (!ids.length) return;
    setNote('');
    setConfirm({ open: true, ids, label });
  };

  const handleSend = async () => {
    if (!confirm.ids.length) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await legalService.sendContractSignatureRequests({
        templateKey: PLATFORM_SERVICE_CONTRACT_KEY,
        audience: 'selected',
        parishIds: confirm.ids,
        note,
      });
      setConfirm({ open: false, ids: [], label: '' });
      setSelectedIds([]);
      setSuccess(`Pedido enviado para ${result.data.sent} organização(ões).`);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Falha ao enviar as solicitações.');
    } finally {
      setSending(false);
    }
  };

  const needsIds = coverage.filter((row) => !row.isCurrent).map((row) => row.parishId);
  const selectedNames = coverage.filter((row) => confirm.ids.includes(row.parishId)).map((row) => row.parishName);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GavelIcon /> Contratos
        </Typography>
        <Typography color="text.secondary">
          Marque as organizações na lista e peça a assinatura. Ou envie de uma vez para quem ainda não assinou.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper>
        <Tabs value={tab} onChange={(_, value) => setTab(value)}>
          <Tab label="Organizações" />
          <Tab label="Assinados" />
          <Tab label="Textos vigentes" />
          <Tab label="Envios" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === 0 && (
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <Chip
                  clickable
                  color={listFilter === 'needs' ? 'warning' : 'default'}
                  variant={listFilter === 'needs' ? 'filled' : 'outlined'}
                  label={`Precisa assinar (${summary.outdated})`}
                  onClick={() => setListFilter('needs')}
                />
                <Chip
                  clickable
                  color={listFilter === 'signed' ? 'success' : 'default'}
                  variant={listFilter === 'signed' ? 'filled' : 'outlined'}
                  label={`Já assinaram (${summary.current})`}
                  onClick={() => setListFilter('signed')}
                />
                <Chip
                  clickable
                  color={listFilter === 'all' ? 'primary' : 'default'}
                  variant={listFilter === 'all' ? 'filled' : 'outlined'}
                  label={`Todas (${summary.total})`}
                  onClick={() => setListFilter('all')}
                />
              </Stack>

              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  bgcolor: selectedVisible.length ? 'action.selected' : 'grey.50',
                }}
              >
                {selectedVisible.length > 0 ? (
                  <>
                    <Typography>
                      <strong>{selectedVisible.length}</strong> marcada(s) na lista.
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Button onClick={() => setSelectedIds([])}>Limpar</Button>
                      <Button
                        variant="contained"
                        onClick={() =>
                          openConfirm(
                            selectedVisible,
                            `Enviar o contrato para as ${selectedVisible.length} organizações marcadas?`
                          )
                        }
                      >
                        Pedir assinatura das marcadas
                      </Button>
                    </Stack>
                  </>
                ) : (
                  <>
                    <Typography>
                      {listFilter === 'needs'
                        ? `${summary.outdated} organizações ainda não assinaram a versão ${summary.currentVersion || '1.0'}.`
                        : 'Marque linhas na tabela para enviar só para algumas.'}
                    </Typography>
                    <Button
                      variant="contained"
                      disabled={!needsIds.length}
                      onClick={() =>
                        openConfirm(
                          needsIds,
                          `Enviar o contrato para as ${needsIds.length} organizações que ainda não assinaram?`
                        )
                      }
                    >
                      Pedir assinatura de quem falta
                    </Button>
                  </>
                )}
              </Paper>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allVisibleSelected}
                        indeterminate={selectedVisible.length > 0 && !allVisibleSelected}
                        onChange={toggleAllVisible}
                      />
                    </TableCell>
                    <TableCell>Organização</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Versão assinada</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => (
                    <TableRow key={row.parishId} hover selected={selectedIds.includes(row.parishId)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedIds.includes(row.parishId)} onChange={() => toggleOne(row.parishId)} />
                      </TableCell>
                      <TableCell>
                        {row.parishName}
                        {(row.city || row.state) && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {[row.city, row.state].filter(Boolean).join(' / ')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{row.email || '—'}</TableCell>
                      <TableCell>{row.signedVersion || 'Nenhuma'}</TableCell>
                      <TableCell>
                        {row.isCurrent ? (
                          <Chip size="small" color="success" label="Assinado" />
                        ) : (
                          <Chip size="small" color="warning" label="Falta assinar" />
                        )}
                        {row.pendingRequest && (
                          <Chip size="small" sx={{ ml: 0.5 }} label="E-mail já enviado" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {row.signedDocumentId && (
                          <>
                            <Button size="small" onClick={() => openViewSigned(row.signedDocumentId!)}>
                              Ver
                            </Button>
                            <Button size="small" onClick={() => downloadSigned(row.signedDocumentId!, row.parishName)}>
                              PDF
                            </Button>
                          </>
                        )}
                        {!row.isCurrent && (
                          <Button
                            size="small"
                            onClick={() => openConfirm([row.parishId], `Enviar o contrato para ${row.parishName}?`)}
                          >
                            Pedir assinatura
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {tab === 1 && (
            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Organização</TableCell>
                  <TableCell>Assinante</TableCell>
                  <TableCell>Versão</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {signedDocs.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>{doc.organizationName || '—'}</TableCell>
                    <TableCell>
                      {doc.signerName}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {doc.signerEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>{doc.templateVersion}</TableCell>
                    <TableCell>{formatDate(doc.signedAt)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openViewSigned(doc.id)}>
                        Ver
                      </Button>
                      <Button size="small" onClick={() => downloadSigned(doc.id, doc.organizationName || doc.templateKey)}>
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {tab === 2 && (
            <Box sx={{ mt: 3 }}>
              {catalog.map((template) => (
                <Paper key={template.key} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6">{template.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.key} · versão {template.version} · {template.kind === 'contract' ? 'Contrato' : 'Consentimento'}
                  </Typography>
                  <LegalBlocks blocks={template.blocks} />
                </Paper>
              ))}
            </Box>
          )}

          {tab === 3 && (
            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Organização</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell>Versão</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Enviado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.parishName}</TableCell>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>{item.templateVersion}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={item.status === 'pending' ? 'Aguardando' : item.status === 'signed' ? 'Assinado' : item.status}
                        color={item.status === 'signed' ? 'success' : item.status === 'pending' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(item.sentAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      <Dialog open={confirm.open} onClose={() => setConfirm({ open: false, ids: [], label: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar envio</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>{confirm.label}</Typography>
          {selectedNames.length > 0 && selectedNames.length <= 8 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedNames.join(', ')}
            </Typography>
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Recado no e-mail (opcional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm({ open: false, ids: [], label: '' })}>Cancelar</Button>
          <Button variant="contained" disabled={sending} onClick={handleSend}>
            {sending ? 'Enviando...' : `Enviar e-mail (${confirm.ids.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{viewTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {viewMeta}
          </Typography>
          <LegalBlocks blocks={viewBlocks} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminLegalContracts;
